import { addMonths, addYears, differenceInCalendarDays, format } from 'date-fns'
import { UTCDate } from '@date-fns/utc'
import type { BridgeLetter, ValidityInfo, ValidityStatus } from '../types/api'

// Validity thresholds
const CAUTION_THRESHOLD_DAYS = 14 // Expires within 2 weeks = caution
const INVALID_THRESHOLD_DAYS = 90 // Expired more than 3 months ago = invalid

/**
 * Calculate validity status for a compliance report based on audit period end date.
 * All date calculations use UTC to ensure consistent behavior across timezones.
 * - "valid": Report is still valid and not expiring within the next 2 weeks
 * - "caution": Report is expiring within 2 weeks, or has expired less than 3 months ago
 * - "invalid": Report has expired more than 3 months ago
 * - "unknown": No audit period end date available
 */
export function calculateValidityStatus(auditPeriodEnd: string | null | undefined): ValidityInfo {
  if (!auditPeriodEnd) {
    return {
      status: 'unknown',
      daysRemaining: 0,
      expirationDate: null,
      message: 'Unknown',
    }
  }

  // Use UTCDate for consistent timezone-independent calculations
  const endDate = new UTCDate(auditPeriodEnd)
  const expirationDate = addYears(endDate, 1)
  const today = new UTCDate()
  const daysRemaining = differenceInCalendarDays(expirationDate, today)

  const formatExpiration = (date: Date) => format(date, 'MMM d, yyyy')

  // Valid: not expiring within 2 weeks
  if (daysRemaining > CAUTION_THRESHOLD_DAYS) {
    return {
      status: 'valid',
      daysRemaining,
      expirationDate,
      message: `Valid until ${formatExpiration(expirationDate)}`,
    }
  }

  // Caution: expiring within 2 weeks OR expired less than 3 months ago
  if (daysRemaining > -INVALID_THRESHOLD_DAYS) {
    if (daysRemaining > 0) {
      return {
        status: 'caution',
        daysRemaining,
        expirationDate,
        message: `Expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`,
      }
    } else {
      const daysExpired = Math.abs(daysRemaining)
      return {
        status: 'caution',
        daysRemaining,
        expirationDate,
        message: `Expired ${daysExpired} day${daysExpired !== 1 ? 's' : ''} ago`,
      }
    }
  }

  // Invalid: expired more than 3 months ago
  return {
    status: 'invalid',
    daysRemaining,
    expirationDate,
    message: `Expired ${formatExpiration(expirationDate)}`,
  }
}

// ============================================================================
// Bridge Letter validity / status
// ============================================================================

/** Badge variants, matching the frontend `StatusVariant` literal union. */
export type ComplianceVariant = 'success' | 'warning' | 'info' | 'error' | 'neutral'

/** Combined compliance status for the badge and the Drata upload gate. */
export interface ComplianceStatus {
  label: string
  variant: ComplianceVariant
  /** Whether the review may still generate documents / be pushed to Drata. */
  isUploadable: boolean
}

const VALIDITY_VARIANT: Record<ValidityStatus, ComplianceVariant> = {
  valid: 'success',
  caution: 'warning',
  invalid: 'error',
  unknown: 'neutral',
}

/**
 * The 6-month cap for a bridge letter: `addMonths(expiration, 6)` where
 * `expiration = addYears(auditPeriodEnd, 1)` — the same anchor
 * `calculateValidityStatus` uses. A bridge letter's `endDate` may never exceed
 * this, and once `today > cap` the review is blocked even with a bridge letter.
 */
export function bridgeLetterCapDate(auditPeriodEnd: string): Date {
  const expiration = addYears(new UTCDate(auditPeriodEnd), 1)
  return addMonths(expiration, 6)
}

/**
 * A bridge letter is currently valid when `start <= today <= end` (inclusive,
 * at calendar-day granularity). `today` defaults to now (mockable via fake
 * timers, matching `calculateValidityStatus`).
 */
export function isBridgeLetterCurrentlyValid(
  bridgeLetter: Pick<BridgeLetter, 'startDate' | 'endDate'>,
  today: Date = new UTCDate()
): boolean {
  const now = new UTCDate(today)
  const start = new UTCDate(bridgeLetter.startDate)
  const end = new UTCDate(bridgeLetter.endDate)
  return differenceInCalendarDays(now, start) >= 0 && differenceInCalendarDays(end, now) >= 0
}

/**
 * Single combiner the UI badge and the Drata gate both call.
 *
 * - `isUploadable = validity.status !== 'invalid' || hasValidBridgeLetter` — a
 *   valid bridge letter keeps an otherwise-invalid (day 90+) review uploadable.
 * - When a valid bridge letter overrides an expired report (report past its
 *   expiration) it returns the `Bridge Letter until {MMM d, yyyy}` label.
 * - Past the 6-month cap (`today > cap`) the review is blocked even with a
 *   bridge letter (defensive: a valid letter can never legitimately have an
 *   `endDate > cap`, so this only bites malformed data).
 * - `isLatestReview` gates only the *label*, not uploadability: the bridge
 *   label is suppressed when a newer approved review exists for the vendor.
 *   Callers compute this (e.g. via the existing `auditPeriodEnd DESC` logic)
 *   and pass it in, keeping this function free of DB access. Defaults to `true`.
 */
export function resolveComplianceStatus(params: {
  auditPeriodEnd: string | null | undefined
  bridgeLetter: Pick<BridgeLetter, 'startDate' | 'endDate'> | null | undefined
  isLatestReview?: boolean
}): ComplianceStatus {
  const { auditPeriodEnd, bridgeLetter, isLatestReview = true } = params
  const validity = calculateValidityStatus(auditPeriodEnd)

  const now = new UTCDate()
  const cap = auditPeriodEnd ? bridgeLetterCapDate(auditPeriodEnd) : null
  const pastCap = cap !== null && differenceInCalendarDays(now, cap) > 0

  const hasValidBridgeLetter = bridgeLetter != null && isBridgeLetterCurrentlyValid(bridgeLetter, now) && !pastCap

  const isUploadable = validity.status !== 'invalid' || hasValidBridgeLetter

  // The bridge letter only overrides once the report itself is expired.
  const reportExpired = validity.status !== 'unknown' && validity.daysRemaining <= 0

  if (bridgeLetter != null && hasValidBridgeLetter && reportExpired && isLatestReview) {
    const end = new UTCDate(bridgeLetter.endDate)
    return {
      label: `Bridge Letter until ${format(end, 'MMM d, yyyy')}`,
      variant: 'info',
      isUploadable,
    }
  }

  return {
    label: validity.message,
    variant: VALIDITY_VARIANT[validity.status],
    isUploadable,
  }
}

/**
 * The default useful window a freshly-uploaded bridge letter gets: valid from
 * the report's expiration (`auditPeriodEnd + 1y`) for one month. Shared by the
 * upload route (which stores it) and the upload modal (which shows it), so the
 * two sides cannot drift. More time is added afterward via extend, in 30-day
 * steps up to `bridgeLetterCapDate`. Returns `YYYY-MM-DD` strings, matching the
 * `date` columns.
 */
export function bridgeLetterDefaultWindow(auditPeriodEnd: string): {
  startDate: string
  endDate: string
} {
  const expiration = addYears(new UTCDate(auditPeriodEnd), 1)
  return {
    startDate: format(expiration, 'yyyy-MM-dd'),
    endDate: format(addMonths(expiration, 1), 'yyyy-MM-dd'),
  }
}
